/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {CancelToken} from 'axios';
import isNil from 'lodash.isnil';
import qs from 'qs';
import {
  EntityAssetType,
  EntityStatus,
  GeoFenceAttributes
} from '../types/entities';
import {
  ArcGISSearchMapAddressCandidatesProps,
  ArcGISSearchMapSuggestProps,
  ArcGISServer,
  ArcGISService,
  AssetAndCountType,
  GeofenceFilter,
  GeoFenceOperation,
  LoadGeofenceEventsParams,
  LoadGeofencesParams,
  LoadGeofencesResult,
  Module
} from '../types/services';
import {
  getQueryString,
  toProperties
} from '../utils/ObjectUtil';
import {
  stringWithEscapeCharacters
} from '../utils/StringUtil';
import {
  client,
  config,
  createAttributeQueryParams,
  createDateTimeFilter,
  createEndpoint,
  createGeoFenceEndpoint,
  createGeofenceQuery,
  createSCDeviceQuery,
  createSearchQuery,
  createShipmentHistoryQuery,
  createV2SearchPayload,
  CVSQueryArgs,
  filterAttributes,
  FindNearbyQueryArgs,
  FM_CVS_FIELDS,
  FM_GEOFENCE_FIELDS,
  GeofenceQueryArgs,
  HVSQueryArgs,
  includeDevices,
  normalizeGeofenceEntity,
  normalizeQueryParams,
  parse,
  parseCollection,
  TRACKING2_HVS_FIELDS
} from './ArcGISUtils';
import {loadAll, simulatedBulkOperation} from './Connect';
import {applyInterceptor} from './ReadOnlyInterceptor';

// Legend request separator
const LEGEND_SEPARATOR = '/0';
let restrictions;

/**
 * setArgGISReadOnlyMode sets a restriction to provided restrctions within the function
 * by subscribing to intercepter with applyInterceptor function.
 *
 * ```typescript
 * import setArgGISReadOnlyMode from '@ion/core/src/services/ArcGIS';
 *
 * setArgGISReadOnlyMode(true) // sets restrction on defined urls in restrction array
 *
 * ```
 */
export const setArgGISReadOnlyMode = (isReadOnly = false) => {
  if (isReadOnly) {
    restrictions = [
      createGeoFenceEndpoint(GeoFenceOperation.update),
      createEndpoint(ArcGISService.Landmarks, ArcGISServer.FeatureServer),
      createGeoFenceEndpoint(GeoFenceOperation.add),
      `${config.arcGISServiceAPIPrefix}/api/geofenceevent/alertStatus`,
      `${config.arcGISServiceAPIPrefix}/api/geofenceevent/:id`,
      `${config.arcGISServiceAPIPrefix}/v2/geofenceevent/status`
    ];
    applyInterceptor({client, restrictions, module: Module.ArcGISModule, isReadOnly});
  }
};

const normalizeUrl = (url: string, separator: string): string => {
  return url?.endsWith(separator) ? url?.substring(0, url.length - separator.length) : url;
};

/**
 * @function getWebMapIdFromName
 *
 * Retrieves webMap information based on the query
 *
 * ```typescript
 *
 *    import {getWebMapIdFromName} from '@ion/core/src/services/ArcGIS';
 *
 *    const response = await getWebMapIdFromName(webMapName);
 *
 * ```
 */
export const getWebMapIdFromName = async(webMapName: string): Promise<any> => {
  const query = `type:"Web Map" AND title:"${webMapName}"`;
  const response = await client({
    url    : `${config.arcGISPortalURL}/rest/search?q=${encodeURIComponent(query)}&f=json`,
    method : 'get'
  });
  const {data} = response;
  return data || [];
};

/**
 * @function getOperationalLayers
 *
 * Retrieves all layers for the given webMapId.
 *
 * ```typescript
 *
 *    import {getOperationalLayers} from '@ion/core/src/services/ArcGIS';
 *
 *    const layers = await getOperationalLayers(webMapId);
 *
 * ```
 */
export const getOperationalLayers = async(webMapId: string): Promise<any> => {
  const response = await client({
    url    : `${config.arcGISPortalURL}/content/items/${webMapId}/data?f=json`,
    method : 'get'
  });
  return response?.data ? response?.data?.operationalLayers : [];
};

/**
 * @function getSublayers
 *
 * Retrieves all sub layers for the given url, include the title
 * if the parameter is presented.
 *
 * ```typescript
 *
 *    import {getSublayers} from '@ion/core/src/services/ArcGIS';
 *
 *    const sublayers = await getSublayers(url, title);
 *
 * ```
 */
export const getSublayers = async(url: string, title?: string): Promise<any> => {
  const uri = url.includes(config.arcGISServiceURLPrefix) ? `${config.arcGISProxyURL}?${url}` : url;
  const response = await client({
    url    : `${uri}?f=json`,
    method : 'get'
  });
  const {data}    = response;
  const sublayers = data?.layers || data?.subLayers;

  if (sublayers?.length) {
    return title ? {title, ...sublayers} : sublayers;
  }
  return [];
};

/**
 * @function getLegend
 *
 * Retrieves the legend for each layer for the given url, include the
 * title is the parameter is presented
 *
 * ```typescript
 *    import {getLegend} from '@ion/core/src/services/ArcGIS';
 *
 *    const legend = await getLegend(url, title);
 * ```
 */
export const getLegend = async(url: string, title?: string): Promise<any> => {
  const normaziledURL = normalizeUrl(url, LEGEND_SEPARATOR);
  const uri           = normaziledURL?.includes(config.arcGISServiceURLPrefix) ? `${config.arcGISProxyURL}?${normaziledURL}` : normaziledURL;
  const response = await client({
    url    : `${uri}/legend?f=pjson`,
    method : 'get'
  });
  const {data} = response;
  if (data?.layers?.length) {
    return title ? [title, ...data?.layers] : data?.layers;
  }
  return [];
};

/**
 * getAssetCount
 *
 * Retrieves the assets count from CVS Services for the given accounts (groups).
 *
 * @see CVSQueryArgs
 * @see createCVSQuery
 *
 * ```typescript
 *    import {getAssetCount} from '@ion/core/src/services/ArcGIS';
 *
 *    const assetCount = await getAssetCount(params);
 *
 * ```
 */
export const getAssetCount = async(
  args: CVSQueryArgs,
  cancelToken?: CancelToken,
  includeSciDevice: boolean = false
): Promise<any> => {
  const {
    assetCategories: categories,
    assetStatus,
    assetTypes: types,
    assets,
    geoFences,
    geometry,
    groups,
    routes,
    searchTerm,
    limitResults,
    ops,
    filter,
    searchtype,
    sort = 'assetName ASC'
  } = args;
  const params = normalizeQueryParams({
    orderByFields : `${sort}`,
    filters       : filter,
    ...(createV2SearchPayload({
      categories,
      assetStatus,
      types,
      assets,
      geoFences,
      geometry,
      groups,
      routes,
      searchTerm,
      searchtype,
      ops
    }))
  }, limitResults);
  const response = await client({
    url    : `${config.arcGISServiceAPIPrefix}/v2/cvs/count`,
    method : 'post',
    data   : JSON.stringify(includeDevices(params, includeSciDevice)),
    ...(cancelToken && {cancelToken})
  });
  const data = {...response?.data, type: EntityAssetType.fleet};
  return data;
};

/**
 * getSCDeviceCount
 *
 * Retrieves the SC device count from arcGIS Services for the given accounts (groups).
 *
 * @see CVSQueryArgs
 * @see createSCDeviceQuery
 *
 * ```typescript
 *    import {getSCDeviceCount} from '@ion/core/src/services/ArcGIS';
 *
 *    const {count} = await getSCDeviceCount(params);
 *
 * ```
 */
export const getSCDeviceCount = async(args: CVSQueryArgs): Promise<any> => {
  const {groups, assets, devices} = args || {};
  const response = await client({
    url  : `${config.arcGISServiceAPIPrefix}/v2/cdm/count`,
    data : JSON.stringify({
      accountid       : groups,
      assetid         : assets ? toProperties(assets) : [],
      deviceId        : devices ? toProperties(devices) : [],
      where           : createSCDeviceQuery(args),
      returnCountOnly : true,
      f               : 'json'
    }),
    method : 'post'
  });
  return response?.data;
};

/**
 * getCVSClusters
 *
 * Retrieves the assets single / clusters from CVS Services for the given parameters.
 *
 * @see CVSQueryArgs
 * @see createCVSQuery
 *
 * ```typescript
 *    import {getCVSClusters} from '@ion/core/src/services/ArcGIS';
 *
 *    const response = await getCVSClusters(params);
 *
 * ```
 */
export const getCVSClusters = async(args: CVSQueryArgs): Promise<any> => {
  const {
    assetCategories: categories,
    assetStatus,
    assetTypes: types,
    assets,
    geoFences,
    geometry,
    groups,
    routes,
    searchTerm,
    limitResults,
    ops,
    filter,
    searchtype
  } = args;
  const params = normalizeQueryParams({
    filters : filter,
    ...(createV2SearchPayload({
      categories,
      assetStatus,
      types,
      assets,
      geoFences,
      geometry,
      groups,
      routes,
      searchTerm,
      searchtype,
      ops
    }))
  }, limitResults);
  const response = await client({
    url  : `${config.arcGISServiceAPIPrefix}/v2/cvs/clusters`,
    data : JSON.stringify({
      ...params,
      ...(args?.geometry && {
        geometry       : args.geometry,
        geometryType   : args.geometryType,
        returnGeometry : true,
        outSR          : 102100
      }),
      mapUnitsPerPixel        : args.mapUnitsPerPixel,
      clusterDistanceInPixels : args.clusterDistanceInPixels,
      outFields               : '*',
      f                       : 'json'
    }),
    method : 'post'
  });
  return response;
};

/**
 * getCVSPaginatedAssets
 *
 * Retrieves paginated assets from CVS Services for the given parameters.
 *
 * @see CVSQueryArgs
 * @see createCVSQuery
 *
 * ```typescript
 *    import {getCVSPaginatedAssets} from '@ion/core/src/services/ArcGIS';
 *
 *    const assets = await getCVSPaginatedAssets(args);
 *
 * ```
 */
export const getCVSPaginatedAssets = async(
  args: CVSQueryArgs,
  includeSciDevice: boolean = false,
  cancelToken?: CancelToken
): Promise<any> => {
  const {
    assetCategories: categories,
    assetStatus,
    assetTypes: types,
    assets,
    geoFences,
    geometry,
    groups,
    routes,
    searchTerm,
    limitResults,
    ops,
    startRow,
    pageSize,
    filter,
    searchtype,
    selectedAssetid,
    outFields = '*',
    sort = 'assetName ASC'
  } = args;
  const params = normalizeQueryParams({
    orderByFields : `${sort}`,
    ...(!isNil(pageSize) && !isNil(startRow) ? {
      resultOffset      : `${startRow}`,
      resultRecordCount : `${pageSize}`
    } : {}),
    filters : filter,
    ...(createV2SearchPayload({
      selectedAssetid,
      categories,
      assetStatus,
      types,
      assets,
      geoFences,
      geometry,
      groups,
      routes,
      searchTerm,
      searchtype,
      ops
    })),
    outFields : !includeSciDevice ? outFields : FM_CVS_FIELDS.toString()
  }, limitResults);
  const response = await client({
    url    : `${config.arcGISServiceAPIPrefix}/v2/cvs/${selectedAssetid ? 'searchSelected' : 'search'}`,
    method : 'post',
    data   : JSON.stringify(includeDevices(params, Boolean(includeSciDevice || args.includeSciDevice))),
    ...(cancelToken && {cancelToken})
  });
  return parseCollection(response, ['attributes', 'geometry']);
};

/**
 * Retrieves total asset count and paginated assets from CVS Services for the given parameters.
 *
 * @see CVSQueryArgs
 * @see createCVSQuery
 *
 * ```typescript
 *    import {getCVSAssets} from '@ion/core/src/services/ArcGIS';
 *
 *    const {assets, count} = await getCVSAssets(params);
 *
 * ```
 */

export const getCVSAssetsAndCount = async({
  includeHFMCount = true,
  count = null,
  ...params
}: CVSQueryArgs): Promise<AssetAndCountType> => {
  const {count: totalCount} = includeHFMCount && !count ? (await getAssetCount(params)) : {count};
  const assets  = await getCVSPaginatedAssets(params);
  return {assets, count: totalCount};
};

/**
 * @function getCVSAssets
 *
 * Retrieves all assets from CVS Services for the given accounts (groups).
 *
 * @see CVSQueryArgs
 * @see createCVSQuery
 *
 * ```typescript
 *    import {getCVSAssets} from '@ion/core/src/services/ArcGIS';
 *
 *    const assets = await getCVSAssets(accounts);
 *
 * ```
 */
export const getCVSAssets = async(args: CVSQueryArgs, includeSciDevice: boolean = false): Promise<any> => {
  const {
    assetCategories: categories,
    assetStatus,
    assetTypes: types,
    assets, geoFences,
    geometry,
    groups,
    routes,
    searchTerm,
    searchByIds,
    limitResults,
    filter,
    assetId,
    searchtype,
    outFields = '*',
    sort = 'assetName ASC'
  } = args;
  const params = normalizeQueryParams({
    orderByFields : `${sort}`,
    filters       : filter,
    outFields,
    ...(createV2SearchPayload({
      categories,
      assetStatus,
      types,
      assets,
      geoFences,
      geometry,
      groups,
      routes,
      searchTerm,
      searchByIds,
      assetId,
      searchtype
    }))
  }, limitResults);

  const response = await client({
    url    : `${config.arcGISServiceAPIPrefix}/v2/cvs/search`,
    method : 'post',
    data   : JSON.stringify(includeDevices(params, includeSciDevice))
  });
  return parseCollection(response, ['attributes', 'geometry']);
};

/**
 * The API fetches the row number of a selected asset in a CVS table based on filter and 
 * sort conditions.This function queries the CVS table using the provided filter and
 * sort conditions, and returns the record number of the selected asset. If no record
 * is found, it returns 0.
 * 
 * ```typescript
 * 
 *  import {getCVSAssetPosition} from '@ion/core/src/services/ArcGIS';
 *
 *  const position = await getCVSAssetPosition(accounts);
 *
 * ```
 * @param args - queries to the CVS table.
 * @returns the record number of the selected asset
 */
export const getCVSAssetPosition = async(args: CVSQueryArgs): Promise<any> => {
  const {
    assetCategories: categories,
    assetStatus,
    assetTypes: types,
    assets, geoFences,
    geometry,
    groups,
    routes,
    searchTerm,
    searchByIds,
    limitResults,
    filter,
    assetId,
    searchtype,
    selectedAssetid,
    sort = 'assetName ASC'
  } = args;
  const params = normalizeQueryParams({
    orderByFields : `${sort}`,
    filters       : filter,
    outFields     : '*',
    ...(createV2SearchPayload({
      categories,
      assetStatus,
      types,
      assets,
      geoFences,
      geometry,
      groups,
      routes,
      searchTerm,
      searchByIds,
      assetId,
      searchtype,
      selectedAssetid
    }))
  }, limitResults);

  const response = await client({
    url    : `${config.arcGISServiceAPIPrefix}/v2/cvs/rownumber`,
    method : 'post',
    data   : JSON.stringify(params)
  });
  return response.data?.count;
};

/**
 * @function searchCVSAssets
 *
 * Retrieves all assets from CVS Services for the given accounts (groups) which
 * match the provided searchTerm.
 *
 * @see CVSQueryArgs
 * @see CVSSearchQueryArgs
 * @see createCVSQuery
 *
 * ```typescript
 *    import {searchCVSAssets} from '@ion/core/src/services/ArcGIS';
 *
 *    const assets = await searchCVSAssets('Asset-1', accounts);
 *
 * ```
 *
 */
export const searchCVSAssets = async(searchTerm: string, groups: number[], limitResults?: number): Promise<any> => {
  const results = await getCVSAssets({searchTerm, groups, limitResults});
  return results;
};

/**
 * @function searchCVSDrivers
 *
 * Retrieves all drivers from CVS Services for the given accounts (groups) which
 * match the provided searchTerm.
 *
 * ```typescript
 *    import {searchCVSDrivers} from '@ion/core/src/services/ArcGIS';
 *
 *    const drivers = await searchCVSDrivers('Frank', accounts);
 *
 * ```
 *
 */
export const searchCVSDrivers = async(searchTerm: string, groups: number[], limitResults?: number): Promise<any> => {
  const params = normalizeQueryParams({
    ...(createV2SearchPayload({groups, searchTerm})),
    searchtype : 'driver',
    outFields  : '*'
  }, limitResults);
  const response = await client({
    url    : `${config.arcGISServiceAPIPrefix}/v2/cvs/search`,
    method : 'post',
    data   : JSON.stringify(params)
  });
  return parseCollection(response, ['attributes']);
};

/**
 *
 * Retrieves devices from CVS Services for the given deviceEsn and accounts (groups) which
 * match the provided searchTerm.
 *
 * ```typescript
 *    import {searchCVSFCDevices} from '@ion/core/src/services/ArcGIS';
 *
 *    const devices = await searchCVSFCDevices('9117', accountIds);
 *
 * ```
 *
 */
export const searchCVSFCDevices = async(searchTerm: string, groups: number[], limitResults?: number): Promise<any> => {
  const params = normalizeQueryParams({
    ...(createV2SearchPayload({groups, searchTerm})),
    outFields  : '*',
    searchtype : 'fcdevice'
  }, limitResults);
  const response = await client({
    url    : `${config.arcGISServiceAPIPrefix}/v2/cvs/search`,
    method : 'post',
    data   : JSON.stringify(params)
  });
  return parseCollection(response, ['attributes']);
};

/**
 * @function getAssetHistory
 *
 * Retrieves an Asset's history from HVS Services for the given parameters.
 * By default, an array of CVSAssets is to be provided, as well as a start
 * and end date. In addtion, any other property of the HVSQueryArgs type
 * can be provided.
 *
 * @see HVSQueryArgs
 *
 * ```typescript
 *    import {getAssetHistory} from '@ion/core/src/services/ArcGIS';
 *
 *    const assets = await getAssetHistory({assets, start, end});
 *
 * ```
 */
export const getAssetHistory = async(args: HVSQueryArgs, isTracking2: boolean = false): Promise<any> => {
  const {
    categories,
    types,
    assets,
    geoFences,
    geometry,
    groups,
    routes,
    status : assetStatus,
    start,
    end
  } = args;
  const ops = !isNil(end) ? 'in range' : 'greater than';
  const params: any = {
    orderByFields : 'eventTime ASC, deviceMessageSequenceNumber ASC',
    spatialRel    : 'esriSpatialRelIntersects',
    outFields     : !isTracking2 || geometry ? '*' : TRACKING2_HVS_FIELDS.toString(),
    outSR         : 102100,
    filters       : createDateTimeFilter({start, end, ops, hasEndDate: true}) ?? [],
    ...(createV2SearchPayload({
      categories,
      assetStatus,
      types,
      assets,
      geoFences,
      geometry,
      groups,
      routes
    }))
  };
  const response = await client({
    url    : `${config.arcGISServiceAPIPrefix}/v2/hvs/search`,
    data   : JSON.stringify(params),
    method : 'post'
  });
  return parseCollection(response, ['attributes', 'geometry']);
};

export const loadShipmentHistory = async(args: HVSQueryArgs): Promise<any> => {
  const {groups, assets, devices} = args || {};
  const response = await client({
    url  : `${config.arcGISServiceAPIPrefix}/v2/hdm/search`,
    data : JSON.stringify({
      accountId     : groups,
      assetId       : assets ? toProperties(assets) : [],
      deviceId      : devices ? toProperties(devices) : [],
      where         : createShipmentHistoryQuery(args),
      outFields     : '*',
      f             : 'json',
      messageType   : 'LOCO_EVENT',
      orderByFields : 'message_time ASC',
      unit          : 'esriSRUnit_StatuteMile'
    }),
    method : 'post'
  });
  return response;
};

/**
 * @function findNearby
 *
 * Retrieves all assets that are within the given radius of the specified asset.
 *
 * @see FindNearbyQueryArgs
 *
 * ```typescript
 *    import {findNearby} from '@ion/core/src/services/ArcGIS';
 *
 *    // Retrieves all assets within the given groups that are within
 *    // a 10 mile radius of the asset ...
 *    const assets = await findNearby({asset, accountIds, miles: 10});
 *
 * ```
 */
export const findNearby = async(args: FindNearbyQueryArgs): Promise<any> => {
  const {latitude, longitude, parameters, miles = 50} = args;
  const {
    assetCategories: categories,
    assetStatus,
    assetTypes: types,
    assets,
    geoFences,
    geometry,
    groups,
    routes
  } = parameters;
  const params = {
    ...(createV2SearchPayload({
      categories,
      assetStatus,
      types,
      assets,
      geoFences,
      geometry,
      groups,
      routes
    })),
    geometry : {
      spatialReference : {
        wkid       : 4326,
        latestWkid : 4326
      },
      x : longitude,
      y : latitude
    },
    outFields      : '*',
    returnGeometry : 'true',
    distance       : miles,
    units          : 'esriSRUnit_StatuteMile',
    orderByFields  : 'dbWriteTime ASC',
    spatialRel     : 'esriSpatialRelIntersects',
    geometryType   : 'esriGeometryPoint',
    inSR           : 4326,
    outSR          : 102100
  };
  const response = await client({
    url    : `${config.arcGISServiceAPIPrefix}/v2/cvs/search`,
    data   : JSON.stringify(params),
    method : 'post'
  });
  return parseCollection(response, ['attributes', 'geometry']);
};

/**
 * Retrieves all Geo Fences currently visible to the selected list of groups
 * ids.
 */
export const getGeoFences = async(account: number, ancestorAccountIds?: number[]): Promise<any> => {
  const query = createSearchQuery({accountId: account, ancestorAccountIds});
  return searchGeoFences({query});
};

/**
 * Provides a method which allows for retrieving GeoFences in Ancestor (non-restricted)
 * accounts and Descendants accounts for a given Account by Id.
 *
 * @param ancestorAccountIds The ancestor account id that needs to be passed.
 * @param descendantAccountId The descendant account id that needs to be passed.
 *
 */
export const loadLandmarkGeoFences = (
  ancestorAccountIds: number[],
  descendantAccountId: number,
  isRootAccount = false
) => {
  if (!isRootAccount) {
    if (ancestorAccountIds?.length) {
      return `landmarkAccountId IN (${descendantAccountId}) or (landmarkAccountId IN (${ancestorAccountIds}) and landmarkRestricted = 'False')`;
    }
    return `landmarkAccountId IN (${descendantAccountId})`;
  } else {
    return '1=0';
  }
};

/**
 * @createGeoFence
 *
 * Creates a new GeoFence based on the provided GeoFenceAttributes and geometry rings,
 * where the attributes are provided via the GeoFenceAttributes values, and the rings
 * define an array of arrays, where each child array contains the given lon / lat
 * coordinates.
 *
 * This method is also used to update an existing geoFence, and is delegated to via
 * the updateGeofence method.
 *
 * @see GeoFenceAttributes
 * @see updateGeoFence
 *
 * ```typescript
 *
 *    import {createGeoFence} from '@ion/core/src/services/ArcGIS';
 *
 *    const attrs = {
 *      landmarkAccount    : 'iOn Suite',
 *      landmarkAccountId  : 15153,
 *      landmarkName       : 'Test Geofence [Create]',
 *      landmarkStatus     : 'Enabled',
 *      geofenceType       : 'Both',
 *      landmarkCategory   : 'Work Site',
 *      landmarkRestricted : 'True'
 *    };
 *    const rings = [
 *      [-9159097.182886813,3656481.184464945],
 *      [-9159150.780331764,3655458.4842917104],
 *      ...];
 *
 *    const geofence = await createGeoFence(attrs, rings);
 *
 * ```
 *
 */
export const createGeoFence = async(
  attributes: GeoFenceAttributes,
  rings: any,
  isUpdate = false
): Promise<any> => {
  const geofence  = normalizeGeofenceEntity(attributes);
  const operation = !isUpdate ? GeoFenceOperation.add : GeoFenceOperation.update;
  const response  = await client({
    method : 'post',
    url    : createGeoFenceEndpoint(operation),
    data   : qs.stringify({
      features : `[${JSON.stringify({
        attributes : geofence,
        geometry   : {
          rings,
          spatialReference : {
            wkid : 102100
          }
        }
      })}]`,
      rollbackOnFailure : true,
      f                 : 'pjson'
    })
  });
  const parsed = parse(response, `${operation}Results`);
  return parsed;
};

/**
 * @updateGeoFence
 *
 * Updates an existing GeoFence based on the provided GeoFenceAttributes and geometry.
 *
 * @see createGeoFenceQueryParams
 * @see GeoFenceAttributes
 *
 * ```typescript
 *
 *    import {createGeoFence} from '@ion/core/src/services/ArcGIS';
 *
 *    const attrs = {
 *      OBJECTID           : landmark.OBJECTID,
 *      landmarkAccount    : 'iOn Suite',
 *      landmarkAccountId  : 15153,
 *      landmarkName       : 'Test Geofence [Update]',
 *      landmarkStatus     : 'Enabled',
 *      geofenceType       : 'Both',
 *      landmarkCategory   : 'Work Site',
 *      landmarkRestricted : 'True',
 *      createdDate        : landmark.createdDate,
 *      modifiedDate       : new Date().getTime()
 *    };
 *    const rings = [
 *      [-9159089.182886813,3656491.184464945],
 *      [-9159171.780331764,3655958.4842917104],
 *      ...];
 *
 *    const geofence = await updateGeoFence(attrs, rings);
 *
 * ```
 *
 */
export const updateGeoFence = async(attributes: GeoFenceAttributes, rings: any): Promise<any> => {
  let geometry = rings;
  if (attributes.geometry) {
    geometry = attributes.geometry.rings;
    delete attributes.geometry;
  }
  const geofence = normalizeGeofenceEntity(attributes);
  const response = await createGeoFence(geofence, geometry, true);
  return response;
};

/**
 *
 * Updates a collection of existing GeoFences based on the provided GeoFenceAttributes and geometry.
 */
export const updateGeoFences = async(attributes: GeoFenceAttributes[]): Promise<any> => {
  return simulatedBulkOperation({
    items : attributes,
    api   : updateGeoFence
  });
};

/**
 * @deleteGeoFence
 *
 * Deletes an existing GeoFence based on the provided GeoFenceAttributes.
 *
 * @see createGeoFenceQueryParams
 * @see GeoFenceAttributes
 *
 * ```typescript
 *
 *    import {createGeoFence} from '@ion/core/src/services/ArcGIS';
 *
 *    const attrs = {
 *      OBJECTID           : landmark.OBJECTID,
 *      landmarkAccount    : 'iOn Suite',
 *      landmarkAccountId  : 15153,
 *      landmarkName       : 'Test Geofence [Update]',
 *      landmarkStatus     : 'Enabled',
 *      geofenceType       : 'Both',
 *      landmarkCategory   : 'Work Site',
 *      landmarkRestricted : 'True',
 *      createdDate        : landmark.createdDate,
 *      modifiedDate       : new Date().getTime()
 *    };
 *    const rings = [
 *      [-9159089.182886813,3656491.184464945],
 *      [-9159171.780331764,3655958.4842917104],
 *      ...];
 *
 *    const geofence = await updateGeoFence(attrs, rings);
 *
 * ```
 *
 */
export const deleteGeoFence = async(attributes: GeoFenceAttributes, rings: any): Promise<any> => {
  const geofence = normalizeGeofenceEntity(attributes);
  const response = await client({
    method : 'post',
    url    : createGeoFenceEndpoint(GeoFenceOperation.update),
    data   : qs.stringify({
      features : `[${JSON.stringify({
        attributes : {
          ...geofence,
          landmarkStatus : EntityStatus.Deleted
        }
      })}]`,
      rollbackOnFailure : true,
      f                 : 'pjson'
    })
  });
  const parsed = parse(response, 'updateResults');
  return parsed;
};

/**
 * Deletes Bulk Admin GeoFences based on the provided GeoFenceAttributes.
 */
export const destroyGeoFences = async(geofences: GeoFenceAttributes[]): Promise<any> => {
  return simulatedBulkOperation({items: geofences, api: deleteGeoFence});
};

/**
 * Performs a GeoFence Search for the given string within the provided account ids.
 */
export const searchGeoFences = async({
  value,
  groupIds,
  query,
  limitResults,
  ignoreCase = false
}: GeofenceQueryArgs): Promise<any> => {
  const url      = createEndpoint(ArcGISService.Landmarks, ArcGISServer.MapServer);
  const resolved = stringWithEscapeCharacters(value);
  const nameLike = ignoreCase
    ? `LOWER(landmarkName) LIKE '%${resolved?.toLowerCase()}%'`
    : `landmarkName LIKE '%${resolved}%'`;
  const response = await client({
    data : qs.stringify(normalizeQueryParams({
      where          : query || `(landmarkAccountId IN (${groupIds.toString()}))${resolved ? ` AND (${nameLike})` : ''}`,
      spatialRel     : 'esriSpatialRelIntersects',
      outFields      : 'landmarkName,OBJECTID,landmarkAccountId',
      returnGeometry : false,
      outSR          : 102100,
      f              : 'json'
    }, limitResults)),
    method : 'post',
    url
  });
  return parseCollection(response, ['attributes']);
};

/**
 * Retrieves all Asset Types currently visible to the selected list of groups
 * and or assets.
 */
export const getAssetTypes = async(
  groupIds?: number[],
  assetIds?: number[]
): Promise<string[]> => {
  const response = await getAttributes(['assetType'], groupIds, assetIds);
  return response;
};

/**
 * Retrieves all Asset Categories currently visible to the selected list of
 * groups and or assets.
 */
export const getAssetCategories = async(
  groupIds?: number[],
  assetIds?: number[]
): Promise<string[]> => {
  const response = await getAttributes(['assetCategory'], groupIds, assetIds);
  return response;
};

/**
 * Retrieves all Routes currently visible to the selected list of
 * groups and or assets.
 */
export const getRouteNames = async(
  groupIds?: number[],
  assetIds?: number[]
): Promise<string[]> => {
  const response = await getAttributes(['routeName'], groupIds, assetIds);
  return response;
};

/**
 * Retrieves and returns each unique Asset Type and Category currently visible
 * to the selected list of groups and or assets.
 */
export const getAssetTypesAndCategories = async(
  groupIds?: number[],
  assetIds?: number[]
): Promise<any> => {
  const response   = await getAttributes(['assetType', 'assetCategory'], groupIds, assetIds);
  const categories = {};
  const types      = {};
  response?.forEach(({assetCategory, assetType}) => {
    assetCategory && (categories[assetCategory] = true);
    assetType && (types[assetType] = true);
  });
  return {
    categories : Object.keys(categories),
    types      : Object.keys(types)
  };
};

/**
 * Retrieves the specified attributes currently visible to the selected list
 * of groups and or assets.
 */
export const getAttributes = async(
  attributes: string[],
  groupIds?: number[],
  assetIds?: number[]
): Promise<any> => {
  const url = createEndpoint(ArcGISService.CVS, ArcGISServer.MapServer);
  const response = await client({
    data   : qs.stringify(createAttributeQueryParams(attributes, groupIds, assetIds)),
    method : 'post',
    url
  });
  return filterAttributes(
    parseCollection(response, ['attributes'], false), attributes
  );
};

/**
 * @function searchMapSuggest
 *
 * The ArcGIS World Geocoding Service includes a method that allows
 * character-by-character autocomplete suggestions to be generated
 * for user input in a client application.
 *
 * @see ArcGISSearchMapSuggestProps
 *
 * ```typescript
 *    import {searchMapSuggest} from '@ion/core/src/services/ArcGIS';
 *
 *    const results = await searchMapSuggest({
 *      text : 'New York'
 *    });
 *    const {suggestions} = results.data;
 *
 * ```
 *
 */
export const searchMapSuggest = async({
  text,
  searchExtent,
  maxSuggestions = 4,
  cancelToken = null
}: ArcGISSearchMapSuggestProps): Promise<any> => {
  let params = {maxSuggestions, text, f: 'json'};
  if (searchExtent) {
    params = Object.assign({}, params, searchExtent);
  }
  const response = await client({
    url    : `https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/suggest?${getQueryString(params)}`,
    method : 'get',
    ...(cancelToken && {cancelToken})
  });
  return response;
};

/**
 * @function searchMapAddressCandidates
 *
 * The ArcGIS World Geocoding Service includes a method that allows
 * character-by-character autocomplete suggestions to be generated
 * for user input in a client application.
 *
 * @see ArcGISSearchMapAddressCandidatesProps
 *
 * ```typescript
 *    import {searchMapAddressCandidates} from '@ion/core/src/services/ArcGIS';
 *
 *    const results = await searchMapAddressCandidates({
 *      magicKey   : magicKey,
 *      singleLine : text
 *    });
 *    const {candidates} = results.data;
 *    const {longitude, latitude} = candidates[0]?.location;
 *
 * ```
 *
 */
export const searchMapAddressCandidates = async({
  magicKey,
  singleLine
}:
ArcGISSearchMapAddressCandidatesProps): Promise<any> => {
  const response = await client({
    url : `https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates?${getQueryString({
      f          : 'json',
      magicKey,
      SingleLine : singleLine
    })}`,
    method : 'get'
  });
  return response;
};

/**
 * @function getTotalGeofenceCount
 *
 * Retrieves the total count for a paginated ArcGIS landmark retrieval.
 */
export const getTotalGeofenceCount = async(
  accountIds: number[],
  filterOn: GeofenceFilter | string,
  ancestorAccountIds: number[]
): Promise<number> => {
  const response = await client({
    url  : createEndpoint(ArcGISService.Landmarks, ArcGISServer.FeatureServer),
    data : qs.stringify({
      where           : createGeofenceQuery(accountIds.toString(), filterOn, ancestorAccountIds),
      spatialRel      : 'esriSpatialRelIntersects',
      returnCountOnly : true,
      f               : 'json'
    }),
    method : 'post'
  });
  return response?.data?.count;
};

/**
 * @function loadGeofences
 *
 * Provides a paginated search implementation against Geofence Services which supports
 * optional sorting, filtering, and hierarchical support.
 *
 * ```typescript
 *
 *    import {loadGeofences} from '@ion/core/src/services/Connect';
 *
 *    // retrieve the first 20 geofences within the given account ...
 *    const {geofences, totalCount} = await loadGeofences({
 *      accountId        : 321,
 *      includeSubGroups : true,
 *      pageId           : 1,
 *      pageSize         : 20
 *    });
 *
 * ```
 *
 * @param   {number} accountId The id of the account from which to retrieve Geofence entities.
 * @param   {number} pageId The page which is to be loaded.
 * @param   {number} pageSize The size of the page to be loaded.
 * @param   {string} sortOn The field on which the results are to be sorted.
 * @param   {string} filterOn An object containing additional searchable properties from
 *          which the searchTerms request is to be based.
 * @param   {includeSubGroups} includeSubGroups When true, indicates that all Geofences
 *          within the account hierarchy are to be returned.
 * @returns Promise<LoadGeofencesResult>
 */
export const loadGeofences = async({
  accountIds,
  ancestorAccountIds,
  resultOffset = 0,
  resultRecordCount = 20,
  isFMTracking = false,
  sortOn = 'landmarkName asc',
  filterOn
}: LoadGeofencesParams): Promise<LoadGeofencesResult> => {
  const url   = createEndpoint(ArcGISService.Landmarks, ArcGISServer.FeatureServer);
  const index = restrictions?.indexOf(url);
  index > -1 && restrictions?.splice(index, 1);
  const query = createGeofenceQuery(accountIds.toString(), filterOn, ancestorAccountIds);
  const params: any = {
    f              : 'json',
    where          : query,
    outFields      : !isFMTracking ? '*' : FM_GEOFENCE_FIELDS.toString(),
    returnGeometry : true,
    outSR          : 102100,
    orderByFields  : sortOn,
    resultOffset,
    resultRecordCount
  };
  const response = await client({
    data   : qs.stringify(params),
    method : 'post',
    url
  });
  const geofences = parseCollection(response, ['attributes', 'geometry'], false);
  if (!isFMTracking) {
    const totalCount = await getTotalGeofenceCount(accountIds, filterOn, ancestorAccountIds);
    return {
      geofences,
      totalCount
    };
  }
  return geofences;
};

/**
 * @function getCVSTrackingAssets
 *
 * Retrieves all assets from CVS Services for the given accounts (groups).
 *
 * @see CVSQueryArgs
 * @see createCVSQuery
 *
 * ```typescript
 *    import {getCVSTrackingAssets} from '@ion/core/src/services/ArcGIS';
 *
 *    const assets = await getCVSTrackingAssets(params);
 *
 * ```
 */
export const getCVSTrackingAssets = async(args: CVSQueryArgs, count = null, limit = 500): Promise<any> => {
  let totalAssets = count;
  const {
    assetCategories: categories,
    assetStatus,
    assetTypes: types,
    assets, geoFences,
    geometry,
    groups,
    routes,
    searchTerm,
    ops,
    filter,
    searchtype,
    includeSciDevice,
    sort = 'assetName ASC'
  } = args;
  if (!totalAssets) {
    const {count: assetsCount} = await getAssetCount(args, undefined, includeSciDevice);
    totalAssets = assetsCount;
  }
  const pages = totalAssets < limit ? 1 : Math.ceil(totalAssets / limit);
  const promises = Array.from({length: pages}, async(_, index) => {
    const response = await client({
      url    : `${config.arcGISServiceAPIPrefix}/v2/cvs/search`,
      method : 'post',
      data   : JSON.stringify(includeDevices({
        ...(createV2SearchPayload({
          categories,
          assetStatus,
          types,
          assets,
          geoFences,
          geometry,
          groups,
          routes,
          searchTerm,
          searchtype,
          ops
        })),
        filters           : filter,
        outFields         : '*',
        orderByFields     : `${sort}`,
        resultOffset      : index ? (index * limit) : 0,
        resultRecordCount : limit
      }, includeSciDevice))
    });
    return parseCollection(response, ['attributes', 'geometry']);
  });
  const results = await Promise.all(promises);
  return results.flat();
};

/**
 * getSCDevices
 *
 * Retrieves the SC Devices for the given parameters.
 *
 * @see CVSQueryArgs
 * @see createSCDeviceQuery
 *
 * ```typescript
 *    import {getSCDevices} from '@ion/core/src/services/ArcGIS';
 *
 *    const scDevices = await getSCDevices(params);
 *
 * ```
 */
export const getSCDevices = async(args: CVSQueryArgs): Promise<any> => {
  const {groups, assets, devices} = args || {};
  const response = await client({
    url  : `${config.arcGISServiceAPIPrefix}/v2/cdm/search`,
    data : JSON.stringify({
      accountid : groups,
      assetid   : assets,
      deviceId  : devices ?? [],
      where     : createSCDeviceQuery(args),
      outFields : '*',
      f         : 'json'
    }),
    method : 'post'
  });
  return response;
};

/**
 * getCargoCount
 *
 * Retrieves the Cargo count and extent for the given parameters.
 *
 * ```typescript
 *    import {getCargoCount} from '@ion/core/src/services/ArcGIS';
 *
 *    const response = await getCargoCount(params);
 * ```
 */
export const getCargoCount = async(args: CVSQueryArgs): Promise<any> => {
  const {groups, assets, devices} = args || {};
  const response = await client({
    url  : `${config.arcGISServiceAPIPrefix}/v2/cdm/count`,
    data : JSON.stringify({
      accountid : groups,
      assetid   : assets,
      deviceId  : devices ?? [],
      where     : createSCDeviceQuery(args),
      outFields : '*',
      f         : 'json'
    }),
    method : 'post'
  });
  const data = {...response?.data, type: EntityAssetType.cargo};
  return data;
};

/*
 * Retrieves  geofenceEvents count based on the provided parameters.
 */
export const loadGeofenceEventsCount = async({
  accountId,
  filterOn,
  includeSubGroups = true
}: LoadGeofenceEventsParams): Promise<any> => {
  const {data:{count}} = await client({
    url    : `${config.arcGISServiceAPIPrefix}/v2/geofenceevent/count`,
    method : 'post',
    data   : JSON.stringify({
      accountid          : accountId,
      includeSubAccounts : includeSubGroups,
      filters            : filterOn
    })
  });
  return count;
};

/**
 * Retrieves all geofenceEvents based on the provided parameters.
 */
export const loadGeofenceEvents = async({
  accountId,
  pageSize,
  pageId,
  filterOn,
  isAccountSearch = false,
  returnGeometry = false,
  sortOn = 'eventTime desc',
  includeSubGroups = true,
  params = null
}: LoadGeofenceEventsParams): Promise<any> => {
  const path = isAccountSearch ? '/v2/geofenceevent/accounts/search' : '/v2/geofenceevent/search';
  const response = await client({
    url    : `${config.arcGISServiceAPIPrefix}${path}`,
    method : 'post',
    data   : JSON.stringify({
      orderByFields      : sortOn,
      outFields          : '*',
      resultOffset       : (pageId - 1) * pageSize,
      resultRecordCount  : pageSize,
      ...(!isAccountSearch && {accountid: accountId}),
      includeSubAccounts : includeSubGroups,
      filters            : filterOn,
      ...(isAccountSearch && createV2SearchPayload(params)),
      returnGeometry
    })
  });
  return parseCollection(response, ['attributes']);
};

/**
 * Acknowledge all geofenceEvents based on the provided parameters.
 */
export const geofenceEventsBulkAction = async(payload): Promise<any> => {
  const response = await client({
    url    : `${config.arcGISServiceAPIPrefix}/v2/geofenceevent/status`,
    method : 'post',
    data   : JSON.stringify(payload)
  });
  return response;
};

/**
 * Function to support exporting feature in Geofence Events grid
 */
export const loadAllGeofenceEvents = async(params): Promise<any> => {
  const geofenceEvents = await loadAll(loadGeofenceEvents, params, 'features');
  return geofenceEvents;
};
